import type {
  WorkflowAgent,
  WorkflowStepValuesType,
} from "@mirage/service-conversation/types";
import { useMemo, useRef } from "react";

export type WorkflowAgentFields = {
  id: string;
  workflowPrompt: string | undefined;
  workflowSteps: WorkflowStepValuesType;
  sources: string[];
  model: string;
  type: string;
  webSearch: boolean | undefined;
  suggestions: boolean | undefined;
};

export const useWorkflowAgentDeps = (
  workflowAgent: WorkflowAgent | undefined,
) => {
  // Store previous deps for comparison
  const prevDepsRef = useRef<WorkflowAgentFields | null>(null);

  // Create stable dependency object with deep comparison
  const workflowAgentDeps: WorkflowAgentFields | null =
    useMemo<WorkflowAgentFields | null>(() => {
      if (!workflowAgent) return null;

      const newDeps: WorkflowAgentFields = {
        id: workflowAgent.id,
        workflowPrompt: workflowAgent.workflow["2"]?.prompt as
          | string
          | undefined,
        workflowSteps: workflowAgent.workflow["3"] as WorkflowStepValuesType,
        sources: workflowAgent.sources,
        model: workflowAgent.model,
        type: workflowAgent.type,
        webSearch: workflowAgent.workflow["2"]?.["web-search"] as
          | boolean
          | undefined,
        suggestions: workflowAgent.workflow["2"]?.suggestions as
          | boolean
          | undefined,
      };

      // Compare with previous value
      if (
        prevDepsRef.current &&
        JSON.stringify(newDeps) === JSON.stringify(prevDepsRef.current)
      ) {
        return prevDepsRef.current;
      }

      prevDepsRef.current = newDeps;
      return newDeps;
    }, [workflowAgent]);

  return workflowAgentDeps;
};
